import { useEffect, useState } from "react"
import "./AnalyticsDashboard.css"

const TOKEN_KEY = "ac_analytics_token"

async function api(path, token) {
  const res = await fetch(`/api/${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error(await res.text().catch(() => "Request failed"))
  return res.json()
}

const COUNTRY_FLAGS = {}
function flag(cc) {
  if (!cc) return ""
  if (COUNTRY_FLAGS[cc]) return COUNTRY_FLAGS[cc]
  const f = [...cc.toUpperCase()].map(c => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0))).join("")
  COUNTRY_FLAGS[cc] = f
  return f
}

function formatNum(n) {
  return Number(n || 0).toLocaleString()
}

export default function AnalyticsDashboard() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [loginError, setLoginError] = useState("")
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [days, setDays] = useState(30)

  async function login(e) {
    e.preventDefault()
    setLoginError("")
    try {
      const res = await fetch("/api/website/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      })
      if (!res.ok) throw new Error("Invalid credentials")
      const d = await res.json()
      const t = d.access_token
      setToken(t)
      localStorage.setItem(TOKEN_KEY, t)
    } catch (err) {
      setLoginError(err.message)
    }
  }

  function logout() {
    setToken("")
    setData(null)
    localStorage.removeItem(TOKEN_KEY)
  }

  useEffect(() => {
    if (!token) return
    setLoading(true)
    Promise.all([
      api(`website/analytics/summary?days=${days}`, token),
      api(`website/analytics/visitors-by-region?days=${days}`, token),
      api(`website/analytics/downloads-by-os?days=${days}`, token),
      api(`website/analytics/top-pages?days=${days}`, token),
      api(`website/analytics/recent?limit=50`, token),
    ])
      .then(([summary, regions, downloads, pages, recent]) => {
        setData({ summary, regions, downloads, pages, recent })
      })
      .catch((err) => {
        if (err.message.includes("401") || err.message.includes("Unauthorized")) {
          logout()
        }
      })
      .finally(() => setLoading(false))
  }, [token, days])

  if (!token) {
    return (
      <div className="ad-shell">
        <form className="ad-login" onSubmit={login}>
          <h2>Analytics Login</h2>
          {loginError && <p className="ad-login-error">{loginError}</p>}
          <label>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          <label>Password</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} required />
          <button type="submit">Sign In</button>
        </form>
      </div>
    )
  }

  if (loading || !data) {
    return <div className="ad-shell"><div className="ad-loading">Loading analytics...</div></div>
  }

  const { summary, regions, downloads, pages, recent } = data

  return (
    <div className="ad-shell">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <h1>Website Analytics</h1>
        <button onClick={logout} style={{ fontSize: 12, color: "var(--ac-text-muted)", background: "none", border: "1px solid var(--ac-border)", borderRadius: 6, padding: "6px 12px", cursor: "pointer" }}>Sign Out</button>
      </div>
      <p>Visitor and download tracking for agentcolab.io</p>

      <div className="ad-days-toggle">
        {[7, 30, 90].map(d => (
          <button key={d} className={days === d ? "active" : ""} onClick={() => setDays(d)}>{d === 7 ? "7 days" : d === 30 ? "30 days" : "90 days"}</button>
        ))}
      </div>

      <div className="ad-cards">
        <div className="ad-card">
          <div className="ad-card-label">Page Views</div>
          <div className="ad-card-value">{formatNum(summary.page_views)}</div>
        </div>
        <div className="ad-card">
          <div className="ad-card-label">Unique Visitors</div>
          <div className="ad-card-value">{formatNum(summary.unique_visitors)}</div>
        </div>
        <div className="ad-card">
          <div className="ad-card-label">Downloads</div>
          <div className="ad-card-value">{formatNum(summary.downloads)}</div>
        </div>
      </div>

      <div className="ad-section">
        <h2>Visitors by Region</h2>
        {regions.length === 0 ? <p style={{ fontSize: 13, color: "#526277" }}>No data yet.</p> : (
          <div className="ad-table-wrap">
            <table className="ad-table">
              <thead><tr><th></th><th>Country</th><th>Visitors</th><th>Page Views</th></tr></thead>
              <tbody>
                {regions.map((r, i) => (
                  <tr key={i}>
                    <td>{flag(r.country_code)}</td>
                    <td>{r.country || "Unknown"}</td>
                    <td>{formatNum(r.visitors)}</td>
                    <td>{formatNum(r.page_views)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="ad-section">
        <h2>Downloads by Platform</h2>
        {downloads.length === 0 ? <p style={{ fontSize: 13, color: "#526277" }}>No downloads yet.</p> : (
          <div className="ad-table-wrap">
            <table className="ad-table">
              <thead><tr><th>Platform</th><th>Downloads</th></tr></thead>
              <tbody>
                {downloads.map((d, i) => (
                  <tr key={i}>
                    <td>{d.platform}</td>
                    <td>{formatNum(d.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="ad-section">
        <h2>Top Pages</h2>
        {pages.length === 0 ? <p style={{ fontSize: 13, color: "#526277" }}>No data yet.</p> : (
          <div className="ad-table-wrap">
            <table className="ad-table">
              <thead><tr><th>Path</th><th>Views</th></tr></thead>
              <tbody>
                {pages.map((p, i) => (
                  <tr key={i}>
                    <td><code>{p.path}</code></td>
                    <td>{formatNum(p.views)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="ad-section">
        <h2>Recent Activity</h2>
        {recent.length === 0 ? <p style={{ fontSize: 13, color: "#526277" }}>No activity yet.</p> : (
          <div className="ad-table-wrap">
            <table className="ad-table">
              <thead><tr><th>Type</th><th>Detail</th><th>Location</th><th>Time</th></tr></thead>
              <tbody>
                {recent.map((e, i) => (
                  <tr key={i}>
                    <td><span className={`ad-type-badge ad-type-${e.type}`}>{e.type === "page_view" ? "View" : "Download"}</span></td>
                    <td>{e.type === "page_view" ? e.path : e.filename || e.platform}</td>
                    <td>{flag(e.country_code || "")} {e.city || e.country || ""}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{new Date(e.created_at + "Z").toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
